import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import cx from "clsx"
import type {
  Bracket,
  ProgramQuestionRow,
  ProgramSummary,
  UpsertProgramQuestion,
} from "shared"
import {
  deleteProgramQuestion,
  fetchAdminPrograms,
  fetchProgramQuestions,
  updateProgramQuestion,
} from "../api"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { AdminButton } from "@rawa/ui/AdminButton"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import {
  isExpectedApiError,
  isForbidden,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ProgramQuestionsPage.module.scss"

// Identifies a row as the store does: an id recurs across programs, and across the
// brackets that program asks it of, so only all three together are unique (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#267).
const keyOf = (q: Pick<ProgramQuestionRow, "programId" | "id" | "bracket">) =>
  `${q.programId} ${q.id} ${q.bracket}`

const BRACKET_ORDER: Bracket[] = ["adult", "teen", "child"]

// aria-describedby is a space-separated list of ids, so the id it points at cannot
// itself contain spaces — keyOf's would be read as several ids, none of which exist.
const errorId = (q: ProgramQuestionRow) =>
  `error-${q.programId}-${q.id}-${q.bracket}`

// A new question, across whichever brackets it's given wording for — composed
// client-side into one PUT per non-blank bracket, since the store itself only
// knows one row per bracket. Any bracket left blank is simply not created,
// which is exactly what a program that doesn't ask a child anything needs.
// A blank order field means "no fixed position" (random), so it is carried as
// text and only parsed to a number (or null) at save time — an empty string
// isn't a valid order to hold in state as anything else.
function parseOrder(raw: string): number | null {
  const trimmed = raw.trim()
  return trimmed === "" ? null : Number(trimmed)
}

type RowSave = { question: ProgramQuestionRow; body: UpsertProgramQuestion }

function without<T>(byKey: Record<string, T>, key: string) {
  if (!(key in byKey)) return byKey
  const rest = { ...byKey }
  delete rest[key]
  return rest
}

function AddQuestionForm({
  programs,
  onAdded,
  onClose,
}: {
  programs: ProgramSummary[]
  onAdded: (row: ProgramQuestionRow) => void
  onClose: () => void
}) {
  const [programId, setProgramId] = useState("")
  const [questionId, setQuestionId] = useState("")
  const [labels, setLabels] = useState<Record<Bracket, string>>({
    adult: "",
    teen: "",
    child: "",
  })
  // One order value applied to every bracket this question is added for — a
  // program asking the same question of several age groups wants it in the same
  // position for each, and nothing here lets one bracket's row be added ahead of
  // the others anyway.
  const [order, setOrder] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const programName = new Map(programs.map((p) => [p.id, p.name]))

  async function save() {
    setError(null)
    const trimmedId = questionId.trim()
    const brackets = BRACKET_ORDER.filter((b) => labels[b].trim() !== "")

    if (!programId) {
      setError("Choose a program.")
      return
    }
    if (trimmedId === "") {
      setError("A question id is required.")
      return
    }
    if (brackets.length === 0) {
      setError("Enter wording for at least one age group.")
      return
    }
    const parsedOrder = parseOrder(order)
    if (
      parsedOrder !== null &&
      (!Number.isInteger(parsedOrder) || parsedOrder < 1)
    ) {
      setError("Order must be a positive whole number, or left blank.")
      return
    }

    setSaving(true)
    try {
      for (const bracket of brackets) {
        const body: UpsertProgramQuestion = {
          programId,
          bracket,
          label: labels[bracket].trim(),
          order: parsedOrder,
        }
        const saved = await updateProgramQuestion(trimmedId, body)
        onAdded({ ...saved, programName: programName.get(programId) ?? null })
      }
      onClose()
    } catch (err) {
      const message = isExpectedApiError(err)
        ? err.message
        : "Couldn't add this question. Please try again."
      if (!isExpectedApiError(err))
        trackError(err, { source: "updateProgramQuestion:add" })
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      aria-label="Add question"
      className={cx(styles.group, styles.addForm)}
    >
      <div className={styles.groupHeader}>
        <span className={styles.eyebrow}>Add question</span>
      </div>
      <div className={styles.addFormBody}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Program</span>
          <select
            className={styles.input}
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
          >
            <option value="">Select a program</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Question id</span>
          <input
            type="text"
            className={styles.input}
            placeholder="prog_example_key"
            value={questionId}
            onChange={(e) => setQuestionId(e.target.value)}
          />
        </label>

        {BRACKET_ORDER.map((bracket) => (
          <label key={bracket} className={styles.field}>
            <span className={styles.fieldLabel}>Wording for {bracket}</span>
            <input
              type="text"
              className={styles.input}
              value={labels[bracket]}
              onChange={(e) =>
                setLabels((prev) => ({ ...prev, [bracket]: e.target.value }))
              }
            />
          </label>
        ))}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Order (optional)</span>
          <input
            type="number"
            min={1}
            step={1}
            className={styles.input}
            placeholder="Leave blank for a random position"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
          />
        </label>

        {error && <ErrorCallout>{error}</ErrorCallout>}

        <div className={styles.addFormActions}>
          <AdminButton variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </AdminButton>
          <AdminButton onClick={() => void save()} disabled={saving}>
            {saving ? "Adding…" : "Add question"}
          </AdminButton>
        </div>
      </div>
    </section>
  )
}

// Manages the program-specific feedback questions across every program, grouped by
// program and edited inline. The questions live in the program-question store (not
// the form definition); only some programs have any.
export function ProgramQuestionsPage() {
  const queryClient = useQueryClient()
  const [savedKey, setSavedKey] = useState<string | null>(null)
  // Keyed by row: a save failure belongs beside the wording that caused it, not at
  // the top of a page listing every program's questions.
  const [errors, setErrors] = useState<Record<string, string>>({})
  // The row a "Delete" click is asking to confirm — replaces the button in place
  // rather than opening a dialog, so the row being removed stays visible.
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null)
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})
  const [addOpen, setAddOpen] = useState(false)
  // A row's wording and order as the admin has edited them, keyed by row; a row
  // without one shows what loaded. The order is held as text so a half-typed or
  // invalid value can sit in the box without corrupting the number/null the API
  // carries, the same reason `errors` is its own map rather than living on the
  // row.
  const [labelEdits, setLabelEdits] = useState<Record<string, string>>({})
  const [orderEdits, setOrderEdits] = useState<Record<string, string>>({})

  // Read each time the page is shown and never in the background. The page
  // needs both, so it shows neither until both have arrived, and the first
  // failure is the page's.
  const questionsQuery = useQuery({
    queryKey: ["program-questions"],
    queryFn: fetchProgramQuestions,
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchProgramQuestions", forbiddenIsExpected: true },
  })
  const programsQuery = useQuery({
    queryKey: ["programs"],
    queryFn: ({ signal }) => fetchAdminPrograms(signal),
    staleTime: Infinity,
    refetchOnMount: "always",
    retry: false,
    meta: { source: "fetchAdminPrograms", forbiddenIsExpected: true },
  })
  const questions = questionsQuery.data ?? []
  const programs = programsQuery.data ?? []
  const loadFailure = questionsQuery.error ?? programsQuery.error
  const loading =
    loadFailure === null &&
    (questionsQuery.isPending || programsQuery.isPending)
  const forbidden = isForbidden(loadFailure)
  const loadError = loadFailure !== null && !forbidden

  const labelOf = (q: ProgramQuestionRow) => labelEdits[keyOf(q)] ?? q.label
  const orderOf = (q: ProgramQuestionRow) =>
    orderEdits[keyOf(q)] ?? (q.order != null ? String(q.order) : "")

  function forget(key: string) {
    setLabelEdits((prev) => without(prev, key))
    setOrderEdits((prev) => without(prev, key))
  }

  const saveRow = useMutation({
    mutationFn: ({ question, body }: RowSave) =>
      updateProgramQuestion(question.id, body),
    onSuccess: (saved) => {
      // The write response carries no program name; keep the resolved one so
      // the group header doesn't flicker back to the id after a save.
      queryClient.setQueryData<ProgramQuestionRow[]>(
        ["program-questions"],
        (held) =>
          held?.map((q) =>
            keyOf(q) === keyOf(saved)
              ? { ...saved, programName: q.programName }
              : q,
          ),
      )
      forget(keyOf(saved))
      setSavedKey(keyOf(saved))
    },
    onError: (err, { question }) => {
      // A deliberate 4xx (e.g. a blank wording → 422) is shown verbatim so the
      // admin sees exactly what to fix; anything else is an unexpected fault.
      const message = isExpectedApiError(err)
        ? err.message
        : "Couldn't save. Please try again."
      if (!isExpectedApiError(err))
        trackError(err, { source: "updateProgramQuestion" })
      setErrors((prev) => ({ ...prev, [keyOf(question)]: message }))
    },
  })
  const savingKey = saveRow.isPending ? keyOf(saveRow.variables.question) : null

  const deleteRow = useMutation({
    mutationFn: (question: ProgramQuestionRow) =>
      deleteProgramQuestion(question.programId, question.id, question.bracket),
    onSuccess: (_, question) =>
      queryClient.setQueryData<ProgramQuestionRow[]>(
        ["program-questions"],
        (held) => held?.filter((q) => keyOf(q) !== keyOf(question)),
      ),
    onError: (err, question) => {
      if (!isForbidden(err))
        trackError(err, { source: "deleteProgramQuestion" })
      setDeleteErrors((prev) => ({
        ...prev,
        [keyOf(question)]: isForbidden(err)
          ? NOT_AUTHORIZED_MESSAGE
          : "Couldn't delete this question. Please try again.",
      }))
    },
    onSettled: () => setConfirmingKey(null),
  })
  const deletingKey = deleteRow.isPending ? keyOf(deleteRow.variables) : null

  function handleDelete(question: ProgramQuestionRow) {
    setDeleteErrors((prev) => without(prev, keyOf(question)))
    deleteRow.mutate(question)
  }

  // An added row can take the place of one already on the page, so it drops
  // that row's edits and shows what was saved.
  function addedQuestion(saved: ProgramQuestionRow) {
    queryClient.setQueryData<ProgramQuestionRow[]>(
      ["program-questions"],
      (held) => [
        ...(held ?? []).filter((q) => keyOf(q) !== keyOf(saved)),
        saved,
      ],
    )
    forget(keyOf(saved))
  }

  function editLabel(question: ProgramQuestionRow, value: string) {
    setLabelEdits((prev) => ({ ...prev, [keyOf(question)]: value }))
    setSavedKey(null)
    clearError(question)
  }

  function editOrder(question: ProgramQuestionRow, value: string) {
    setOrderEdits((prev) => ({ ...prev, [keyOf(question)]: value }))
    setSavedKey(null)
    clearError(question)
  }

  function clearError(question: ProgramQuestionRow) {
    setErrors((prev) => without(prev, keyOf(question)))
  }

  function save(question: ProgramQuestionRow) {
    const k = keyOf(question)
    const order = parseOrder(orderOf(question))
    if (order !== null && (!Number.isInteger(order) || order < 1)) {
      setErrors((prev) => ({
        ...prev,
        [k]: "Order must be a positive whole number, or left blank.",
      }))
      return
    }
    clearError(question)
    saveRow.mutate({
      question,
      body: {
        programId: question.programId,
        // Preserved, not chosen: picking the age group is its own piece of work.
        bracket: question.bracket,
        label: labelOf(question),
        order,
      },
    })
  }

  // Group by program, then by question, preserving the API's (programId, id)
  // ordering. Within a question the brackets are put in age order so a wording
  // can be read against the one above it.
  const groups = new Map<string, Map<string, ProgramQuestionRow[]>>()
  for (const q of questions) {
    const program = groups.get(q.programId) ?? new Map()
    program.set(q.id, [...(program.get(q.id) ?? []), q])
    groups.set(q.programId, program)
  }
  for (const program of groups.values())
    for (const rows of program.values())
      rows.sort(
        (a, b) =>
          BRACKET_ORDER.indexOf(a.bracket) - BRACKET_ORDER.indexOf(b.bracket),
      )

  return (
    <div className={styles.page}>
      <div className={styles.titleBlock}>
        <div className={styles.titleRow}>
          <h1 className={cx(typography.h2, styles.heading)}>
            Program questions
          </h1>
          {!loading && !forbidden && !addOpen && (
            <AdminButton onClick={() => setAddOpen(true)}>
              Add question
            </AdminButton>
          )}
        </div>
        <p className={styles.subtitle}>
          Add, edit or remove the program-specific questions shown alongside the
          standard feedback ratings. Grouped by program — the ICIS program name
          is shown where known, otherwise its id. A program with none yet gets
          its first one from Add question above.
        </p>
      </div>

      {addOpen && !loading && !forbidden && (
        <AddQuestionForm
          programs={programs}
          onAdded={addedQuestion}
          onClose={() => setAddOpen(false)}
        />
      )}

      {loading && (
        <div
          role="status"
          aria-label="Loading program questions"
          className={styles.group}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <span className={styles.skeletonLabel} />
              <span className={styles.skeletonButton} />
            </div>
          ))}
        </div>
      )}

      {!loading && forbidden && (
        <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>
      )}

      {!loading && loadError && (
        <ErrorCallout>Failed to load program questions.</ErrorCallout>
      )}

      {!loading && !loadError && !forbidden && questions.length === 0 && (
        <p className={styles.empty}>
          No program questions yet — add one above.
        </p>
      )}

      {!loading &&
        !loadError &&
        !forbidden &&
        [...groups].map(([programId, byQuestion]) => {
          // Every row in a group shares the same program, so any carries its name.
          const programName =
            byQuestion.values().next().value?.[0]?.programName ?? null
          return (
            <section
              key={programId}
              role="group"
              aria-label={`Program ${programName ?? programId}`}
              className={styles.group}
            >
              <div className={styles.groupHeader}>
                <span className={styles.eyebrow}>Program</span>
                <span className={styles.programId} title={programId}>
                  {programName ?? programId}
                </span>
                <span className={styles.count}>
                  {byQuestion.size}{" "}
                  {byQuestion.size === 1 ? "question" : "questions"}
                </span>
              </div>
              {[...byQuestion].map(([questionId, bracketRows]) => (
                <section
                  key={questionId}
                  role="group"
                  aria-label={`Question ${questionId}`}
                  className={styles.question}
                >
                  <span className={styles.qid}>{questionId}</span>
                  <ul className={styles.list}>
                    {bracketRows.map((q) => (
                      <li
                        key={keyOf(q)}
                        aria-label={`${q.id} asked of ${q.bracket}`}
                        className={styles.row}
                      >
                        <div className={styles.rowMain}>
                          <span className={styles.bracketName}>
                            asked of {q.bracket}
                          </span>
                          <input
                            type="text"
                            aria-label={`Wording for ${q.id} asked of ${q.bracket}`}
                            aria-invalid={Boolean(errors[keyOf(q)])}
                            aria-describedby={
                              errors[keyOf(q)] ? errorId(q) : undefined
                            }
                            className={cx(styles.input, {
                              [styles.inputInvalid]: errors[keyOf(q)],
                            })}
                            value={labelOf(q)}
                            onChange={(e) => editLabel(q, e.target.value)}
                          />
                          <input
                            type="number"
                            min={1}
                            step={1}
                            aria-label={`Order for ${q.id} asked of ${q.bracket}`}
                            placeholder="Random"
                            title="Order — leave blank for a random position"
                            className={cx(styles.input, styles.orderInput)}
                            value={orderOf(q)}
                            onChange={(e) => editOrder(q, e.target.value)}
                          />
                          <div className={styles.control}>
                            {savedKey === keyOf(q) && (
                              <span role="status" className={styles.saved}>
                                Saved
                              </span>
                            )}
                            <button
                              type="button"
                              className={styles.saveButton}
                              onClick={() => save(q)}
                              disabled={savingKey === keyOf(q)}
                            >
                              {savingKey === keyOf(q) ? "Saving…" : "Save"}
                            </button>
                            {confirmingKey === keyOf(q) ? (
                              <>
                                <span className={styles.confirmText}>
                                  Delete this question?
                                </span>
                                <button
                                  type="button"
                                  className={styles.action}
                                  onClick={() => setConfirmingKey(null)}
                                  disabled={deletingKey === keyOf(q)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className={cx(
                                    styles.action,
                                    styles.actionDanger,
                                  )}
                                  onClick={() => handleDelete(q)}
                                  disabled={deletingKey === keyOf(q)}
                                >
                                  {deletingKey === keyOf(q)
                                    ? "Deleting…"
                                    : "Delete"}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className={styles.action}
                                onClick={() => setConfirmingKey(keyOf(q))}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        {errors[keyOf(q)] && (
                          <span
                            id={errorId(q)}
                            role="alert"
                            className={styles.fieldError}
                          >
                            {errors[keyOf(q)]}
                          </span>
                        )}
                        {deleteErrors[keyOf(q)] && (
                          <span role="alert" className={styles.fieldError}>
                            {deleteErrors[keyOf(q)]}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </section>
          )
        })}
    </div>
  )
}
